
import { toast } from 'sonner';
import { getStripeInstance } from '../services/stripe/scriptLoader';
import { supabase } from '@/integrations/supabase/client';
import { Transaction } from '@/types/payment';

// Payment details interface
export interface StripePaymentRequest {
  amount: number;
  currency?: string;
  recipientEmail: string;
  description?: string;
  senderEmail: string;
  senderName: string;
  cardDetails: {
    cardNumber: string;
    expiryDate: string;
    cvv: string;
    cardholderName?: string;
  };
}

export interface StripePaymentResponse {
  success: boolean;
  transactionId?: string;
  error?: string;
}

/**
 * Process a payment through Stripe
 */
export const processStripePayment = async (
  paymentRequest: StripePaymentRequest
): Promise<StripePaymentResponse> => {
  try {
    console.log('Processing Stripe payment...', paymentRequest);
    
    // Create payment method if card details are provided
    const stripe = await getStripeInstance();
    if (!stripe) {
      throw new Error('Stripe is not initialized');
    }
    
    const [month, year] = paymentRequest.cardDetails.expiryDate.split('/');
    
    const pmResult = await stripe.createPaymentMethod({
      type: 'card',
      card: {
        number: paymentRequest.cardDetails.cardNumber,
        exp_month: parseInt(month),
        exp_year: parseInt(`20${year}`),
        cvc: paymentRequest.cardDetails.cvv
      },
      billing_details: {
        name: paymentRequest.cardDetails.cardholderName || paymentRequest.senderName
      }
    });
    
    if (pmResult.error) {
      throw new Error(pmResult.error.message);
    }
    
    const paymentMethodId = pmResult.paymentMethod.id;
    
    // Call Supabase edge function to process the payment
    const { data, error } = await supabase.functions.invoke('stripe-payment', {
      body: {
        payment_method_id: paymentMethodId,
        amount: Math.round(paymentRequest.amount * 100), // Convert to cents for Stripe
        currency: paymentRequest.currency || 'usd',
        description: paymentRequest.description || `Payment to ${paymentRequest.recipientEmail}`,
        metadata: {
          senderEmail: paymentRequest.senderEmail,
          recipientEmail: paymentRequest.recipientEmail
        }
      }
    });
    
    if (error) {
      console.error('Supabase function error:', error);
      throw new Error(error.message || 'Payment processing failed');
    }
    
    if (!data || !data.success) {
      throw new Error((data && data.error) || 'Payment processing failed');
    }
    
    // Create the transaction record
    const transaction: Transaction = {
      id: data.paymentIntentId || `tx_${Date.now()}`,
      amount: paymentRequest.amount,
      fee: paymentRequest.amount * 0.029 + 0.30, // Standard Stripe fee
      recipient: paymentRequest.recipientEmail,
      recipientEmail: paymentRequest.recipientEmail,
      sender: paymentRequest.senderName,
      senderEmail: paymentRequest.senderEmail,
      status: 'completed',
      method: 'stripe',
      timestamp: new Date().toISOString(),
      metadata: {
        payment_processor: 'stripe',
        payment_intent_id: data.paymentIntentId
      }
    };
    
    // Store locally for offline access
    localStorage.setItem(`transaction_${transaction.id}`, JSON.stringify(transaction));
    
    return {
      success: true,
      transactionId: transaction.id
    };
  } catch (error) {
    console.error('Stripe payment error:', error);
    return {
      success: false,
      error: error.message || 'An unexpected error occurred during payment processing'
    };
  }
};
